import { appWithTranslation, useTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import ErrorBoundary from 'components/ErrorBoundary';
import DatadogRumInit from 'lib/client/datadogRum';
import { LocaleNS } from 'lib/client/locale-ns';
import RouteListener from 'lib/client/RouteListener';
import { GlobalStyles } from 'styles/globals';
import { TestIdBoundaryProvider } from '@honeybook/hbui';
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { chartBase } from 'components/common/charts/chartBase';
import GlobalProviders from 'providers/GlobalProviders';
import { AuthLinkProvider } from 'lib/comlink/AuthLink';
import { SideNavHandler } from 'components/SideNavSupport/SideNavHandler';
import { ColorModeHandler } from 'components/ColorModeSupport/ColorModeHandler';
import { useSideNavSupport } from 'components/SideNavSupport/useSideNavSupport';
import { useMonitorActivity } from '../lib/client/monitorActivity';
import { FinanceServerProps } from '../lib/getFinanceServerProps';
import getConfig from 'next/config';
import { logger } from '@finance/shared';
import { useFontGateControl } from '../lib/client/hooks/useFontGateControl';
import { SessionProvider } from 'next-auth/react';
import { useEffect } from 'react';
import i18next from 'i18next';
import { registerFormatters, setDefaultCurrency } from '@honeybook/i18n';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, PointElement, LineElement, annotationPlugin, ArcElement);
chartBase(ChartJS);

// Register the i18next `currency` and `currencyCode` formatters once, after
// next-i18next finishes its lazy `i18next.init()`. Module-top-level call
// races SSR page-data collection (i18next.services.formatter is undefined),
// so we wait for the `initialized` event (or run immediately if already
// initialized).
function useRegisterCurrencyFormatters() {
    useEffect(() => {
        if (i18next.isInitialized) {
            registerFormatters();
            return;
        }
        const onInit = () => registerFormatters();
        i18next.on('initialized', onInit);
        return () => {
            i18next.off('initialized', onInit);
        };
    }, []);
}

const publicPaths = ['/auth/token', '/auth/embedded', '/terms/[doc_name]', '/403'];
const appPaths: Record<string, string> = {
    '/expenses': 'expenses page'
};

function MyApp({ Component, pageProps: { session, uiPreferences, gates = {}, ...pageProps } }: AppProps<FinanceServerProps>) {
    const { t } = useTranslation(['common']);
    const { pathname, query } = useRouter();
    let appPath = appPaths[pathname];
    const clientOnlyPaths = getConfig().publicRuntimeConfig.clientOnlyRoutes as string[];

    logger.debug(
        {
            clientOnlyPaths,
            pathname
        },
        'Client Only config'
    );

    if (appPath && query.source) {
        appPath = query.source as string;
    }
    useSideNavSupport();
    useMonitorActivity();
    useFontGateControl(gates?.rebrand_new_fonts);
    useRegisterCurrencyFormatters();

    useEffect(() => {
        const currency = session?.user?.company?.currency;
        if (currency) {
            setDefaultCurrency(currency);
        }
    }, [session?.user?.company?.currency]);

    if (publicPaths.includes(pathname)) {
        return (
            <>
                <ErrorBoundary>
                    <TestIdBoundaryProvider prefix={'finance'}>
                        <AuthLinkProvider>
                            <SessionProvider session={session} refetchInterval={30 * 60} refetchOnWindowFocus={true}>
                                <SideNavHandler />
                                <ColorModeHandler />
                                <GlobalStyles />
                                <RouteListener />
                                <DatadogRumInit />
                                <Component {...pageProps} gates={gates} />
                            </SessionProvider>
                        </AuthLinkProvider>
                    </TestIdBoundaryProvider>
                </ErrorBoundary>
            </>
        );
    }

    return (
        <>
            <Head>
                <title>{t('top-bar-title', { ns: LocaleNS.common })}</title>
            </Head>
            <GlobalProviders
                session={session}
                appPath={appPath}
                uiPreferences={uiPreferences}
                clientOnly={clientOnlyPaths.includes(pathname)}
            >
                <Component {...pageProps} gates={gates} />
            </GlobalProviders>
        </>
    );
}

export default appWithTranslation(MyApp);
